import { z } from 'zod';
const actor = z.enum(['jordan', 'marcus', 'elena', 'noor', 'amara']);
export const fieldProgressStageSchema = z.enum([
    'agreed',
    'designed',
    'built',
    'verified',
    'controls',
    'shipped',
]);
export const fieldProgressRowIdSchema = z.enum(['knowledge', 'trade', 'recovery', 'handoff']);
export const fieldProgressStateSchema = z.enum([
    'not_started',
    'in_progress',
    'waiting',
    'verified',
    'needs_recheck',
]);
export const fieldProgressEvidenceSchema = z.object({
    id: z.string(),
    kind: z.enum([
        'decision',
        'document',
        'ticket',
        'review',
        'run',
        'acceptance',
        'hold',
        'incident',
        'source',
    ]),
    label: z.string(),
    detail: z.string(),
    revision: z.string().optional(),
    at: z.string().optional(),
    stale: z.boolean().default(false),
});
export const fieldProgressCellSchema = z.object({
    id: z.string(),
    rowId: fieldProgressRowIdSchema,
    stage: fieldProgressStageSchema,
    state: fieldProgressStateSchema,
    summary: z.string(),
    standard: z.string(),
    nextStep: z.string(),
    evidence: z.array(fieldProgressEvidenceSchema),
    waitingOn: z.array(actor),
});
export const fieldProgressChangeSchema = z.object({
    id: z.string(),
    revision: z.number().int().nonnegative(),
    at: z.string(),
    kind: z.enum([
        'decision',
        'review',
        'verification',
        'acceptance',
        'invalidation',
        'release',
        'handoff',
    ]),
    title: z.string(),
    detail: z.string(),
    cellIds: z.array(z.string()),
    evidenceIds: z.array(z.string()),
});
export const fieldProgressRequirementSchema = z.object({
    id: z.string(),
    decisionId: z.string().optional(),
    version: z.number().int().positive(),
    title: z.string(),
    outcomeIds: z.array(z.enum(['knowledge', 'trade', 'recovery'])),
    owner: actor,
    sourceIds: z.array(z.string()),
    sourceRevisions: z.record(z.string(), z.string()),
    publicRequirementIds: z.array(z.string()),
    acceptance: z.array(z.string()),
    history: z.array(z.object({
        version: z.number().int().positive(),
        status: z.enum(['open', 'proposed', 'confirmed', 'deferred']),
        at: z.string(),
    })),
    state: fieldProgressStateSchema,
    confirmedAt: z.string(),
    verifiedAt: z.string().nullable(),
    candidateRevision: z.string().nullable(),
    checkFamilies: z.array(z.string()),
    evidence: z.array(fieldProgressEvidenceSchema),
    nextStep: z.string(),
});
/**
 * A pure projection of learner-visible evidence. It carries no per-response timestamp so that
 * an unchanged snapshot keeps its identity across polls.
 */
export const fieldProgressSchema = z.object({
    version: z.literal('1'),
    snapshotRevision: z.number().int().nonnegative(),
    candidateRevision: z.string().nullable(),
    framing: z.enum(['accumulated', 'checkpoint', 'remaining']),
    readiness: z.object({
        ready: z.boolean(),
        blockerCount: z.number().int().nonnegative(),
        blockers: z.array(z.string()),
        gates: z.array(z.object({
            id: z.string(),
            label: z.string(),
            passed: z.boolean(),
            evidenceIds: z.array(z.string()),
        })),
    }),
    rows: z.array(z.object({
        id: fieldProgressRowIdSchema,
        title: z.string(),
        description: z.string(),
        cells: z.array(fieldProgressCellSchema),
    })),
    waitingOn: z.array(z.object({
        id: z.string(),
        actorId: actor.nullable(),
        title: z.string(),
        detail: z.string(),
        cellIds: z.array(z.string()),
        since: z.string(),
    })),
    changes: z.array(fieldProgressChangeSchema),
    earlierChangesOmitted: z.boolean(),
    lastSeenRevision: z.number().int().nonnegative().nullable(),
    requirements: z.array(fieldProgressRequirementSchema),
});
